import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react"
import { useAuth } from "auth"
import { IconButton, useWindowWidth } from "materia"
import { useTranslation } from "react-i18next"
import { getHasAdminAccess } from "../../../dataUtilities/getHasAdminAccess"
import type { Role } from "../../../graphql"
import { APP_VERSION, IS_APP } from "../../../vuplex/constants"
import {
  canUseSharedDeviceFlag,
  mustUseSharedDeviceFlag,
} from "../../SharedDeviceInactivityTracker"
import { useCurrentCompanyContext } from "../CurrentCompanyProvider"
import { useCurrentUserContext } from "../CurrentUserProvider"
import {
  ButtonDeleteMe,
  ButtonLogout,
  DropdownAbout,
  DropdownCompany,
  DropdownLanguage,
  LinkAdmin,
  LinkHelpCenter,
  LinkLeaderboards,
  LinkYourProfile,
  TogglePushNotifications,
  ToggleSharedDevice,
} from "./HeaderMenuItems"
import s from "./styles.module.scss"

// Should be refactored be opened from the outside
export const HeaderMenuConnected = () => {
  const { currentUser, isCurrentUserPending } = useCurrentUserContext()
  const { currentCompany, isCurrentCompanyPending } = useCurrentCompanyContext()
  const { activeSession, sessions = [], isPending: isAuthPending } = useAuth()

  const isLoading =
    isCurrentCompanyPending || isCurrentUserPending || isAuthPending

  const companiesWithActiveSession = sessions
    .filter((session) => activeSession?.accessToken !== session.accessToken)
    .map((session) => ({
      companyName: session.companyNameKey,
      companyLogo: session.logo,
      href: `/${session.subdomain}`,
    }))

  return (
    // TODO: [api-decoupling] HeaderMenu should probably be split up such that a menu button can open it from the outside and the the menu list itself is composed of it's items instead
    <HeaderMenu
      currentCompany={currentCompany}
      currentUser={currentUser}
      companies={companiesWithActiveSession}
      isLoading={isLoading}
    />
  )
}

type HeaderMenuProps = {
  isLoading?: boolean
  currentUser: {
    avatar: string
    avatarTitle: string
    position: string | null
    roles: Role[]
  }
  currentCompany: {
    companyFeatureFlags: string[]
    logoUrl: string
    name: string | null
    portalFeatureFlags: string[]
    portalSupportedLocales: string[]
    selfRegistration: boolean
    privacyPolicy: string | null
  }
  companies: {
    companyName: string
    companyLogo: string
    href: string
  }[]
}
export const HeaderMenu = ({
  currentCompany,
  currentUser,
  companies,
  isLoading,
}: HeaderMenuProps) => {
  const { avatar, avatarTitle, position, roles } = currentUser
  const {
    selfRegistration,
    name,
    portalSupportedLocales,
    privacyPolicy,
    logoUrl,
    companyFeatureFlags,
    portalFeatureFlags,
  } = currentCompany

  // isSharedDeviceEnabled is true if either of the flags are present in the BE,
  // making it possible to set the device as shared.
  const isSharedDeviceEnabled = companyFeatureFlags.some((flag) =>
    [canUseSharedDeviceFlag, mustUseSharedDeviceFlag].includes(flag)
  )
  const showHelpCenterLink = !companyFeatureFlags.includes("disable_help_links")
  const showCompanySelectionDropdown = !IS_APP
  const showUserProfileButton = !portalFeatureFlags.includes(
    "disable_user_profile"
  )
  const showLeaderboardLink = !portalFeatureFlags.includes(
    "disable_leaderboard"
  )
  const isSingleLogOutEnabled = companyFeatureFlags.includes(
    "enable_single_log_out"
  )
  const showAdminLink = getHasAdminAccess(roles)

  if (isLoading) return <div className={s.skeleton__headerMenu} />

  return (
    <Popover>
      <PopoverButton
        className={s.headerMenuButton}
        title="Header menu"
        data-testid="header-menu-button"
      />

      <PopoverBackdrop className={s.headerMenuBackdrop} transition />

      <PopoverPanel
        className={s.headerMenuContent}
        anchor={{ to: "bottom end" }}
        transition
        modal
      >
        {({ close }) => (
          <>
            <HeaderMenuHeader onClose={close} />

            {showCompanySelectionDropdown && (
              <DropdownCompany
                companies={companies}
                logoUrl={logoUrl}
                name={name}
              />
            )}

            {showUserProfileButton && (
              <LinkYourProfile
                avatar={avatar}
                avatarTitle={avatarTitle}
                position={position}
                onClick={close}
              />
            )}

            {showLeaderboardLink && <LinkLeaderboards onClick={close} />}

            <DropdownLanguage locales={portalSupportedLocales} />

            {showHelpCenterLink && <LinkHelpCenter onClick={close} />}

            <DropdownAbout privacyPolicy={privacyPolicy} />

            <TogglePushNotifications />

            {isSharedDeviceEnabled && <ToggleSharedDevice />}

            {showAdminLink && <LinkAdmin />}

            <ButtonLogout isSingleLogOutEnabled={isSingleLogOutEnabled} />

            {/* A self registered user is basically an impersonal/"guest" user, that should be allowed to delete themselves */}
            {selfRegistration && <ButtonDeleteMe />}

            <HeaderMenuFooter />
          </>
        )}
      </PopoverPanel>
    </Popover>
  )
}

type HeaderMenuHeaderProps = { onClose: () => void }
const HeaderMenuHeader = ({ onClose }: HeaderMenuHeaderProps) => {
  const { t } = useTranslation()

  const { isSmallWindow } = useWindowWidth()
  if (!isSmallWindow) return null

  return (
    <div className={s.headerMenuHeader}>
      <div className={s.headerMenuHeader__content}>
        <div className={s.headerMenuHeader__title}>{t("MENU")}</div>
        <IconButton
          as="button"
          icon="Close"
          onClick={onClose}
          aria-label="Close menu"
        />
      </div>
    </div>
  )
}

const HeaderMenuFooter = () => {
  const { t } = useTranslation()

  if (!APP_VERSION) return null

  return (
    <div className={s.headerMenuFooter}>
      <div className={s.headerMenuFooter__appVersion}>
        {t("settings.APP_VERSION")} {APP_VERSION}
      </div>
    </div>
  )
}
